import React, { useState, useEffect, useReducer } from "react";
import { Form, Button, DatePicker, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { reducer } from "../../../Utils/CommonUtils";
import { AggTransaction } from "../../../Services/TransactionService";

const ReportPage = ({ history, userData }) => {
  const { RangePicker } = DatePicker;

  // ⚠️ If you ever SSR, guard window
  const isClient = typeof window !== "undefined";
  const [isMobile, setIsMobile] = useState(isClient ? window.innerWidth < 600 : false);

  useEffect(() => {
    if (!isClient) return;
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isClient]);

  const [form] = Form.useForm();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const initialState = { searchKeyword: "", filteredInfo: {}, sorter: {} };
  const [state, dispatch] = useReducer(reducer, initialState);

  // ✅ define before render
  const cardStyle = {
    marginBottom: "20px",
    padding: "15px",
    border: "1px solid #ddd",
    borderRadius: "10px",
    backgroundColor: "#f8f8f8",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    width: "100%",
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let startDate, endDate;
      if (isMobile) {
        startDate = values.startDate;
        endDate = values.endDate;
      } else {
        [startDate, endDate] = values.dateRange || [];
      }
      if (!startDate || !endDate) {
        message.error("Both dates are required!");
        return;
      }
      const formattedStartDate = startDate.format("YYYY-MM-DD");
      const formattedEndDate = endDate.format("YYYY-MM-DD");

      setLoading(true);
      const response = await AggTransaction({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      if (!response || !Array.isArray(response.data)) {
        message.error("Invalid response format from server");
        setLoading(false);
        return;
      }

      setTransactions(response.data);
      setLoading(false);
    } catch (error) {
      message.error("Failed to fetch transactions");
      setLoading(false);
    }
  };

  // robust totals (default missing arrays)
  const totalSummary = transactions.reduce(
    (acc, txn) => {
      acc.totalQuantity += txn.totalQuantity || 0;
      acc.totalSales += txn.totalSales || 0;
      acc.uniqueCounterCount += txn.uniqueCounterCount || 0;
      acc.noSalesCount += txn.noSalesCount || 0;
      acc.NoTransactionCodeCount += txn.NoTransactionCodeCount || 0;

      acc.NoTransactionCode.push(...(txn.NoTransactionCode ?? []));
      acc.noSalesCounters.push(...(txn.noSalesCounters ?? []));
      return acc;
    },
    {
      totalQuantity: 0,
      totalSales: 0,
      uniqueCounterCount: 0,
      noSalesCount: 0,
      NoTransactionCodeCount: 0,
      NoTransactionCode: [],
      noSalesCounters: [],
    }
  );

  const generateReportText = () => {
    const { startDate, endDate, dateRange } = form.getFieldsValue();
    let s, e;
    if (isMobile) {
      s = startDate; e = endDate;
    } else {
      [s, e] = dateRange || [];
    }
    const fmt = (d) => (d?.format ? d.format("YYYY-MM-DD") : "");
    const startStr = fmt(s);
    const endStr = fmt(e);

    let text = "";
    if (startStr || endStr) text += `Periode: ${startStr}${startStr && endStr ? " s/d " : ""}${endStr}\n\n`;

    text += `*ALL*:\n`;
    text += `Total Quantity: ${totalSummary.totalQuantity}\n`;
    text += `Total Outlet Yang Lapor: ${totalSummary.uniqueCounterCount}\n`;
    text += `Total Outlet Yang Jual: ${totalSummary.uniqueCounterCount - totalSummary.noSalesCount}\n`;
    text += `Total Outlet Yang Tidak Lapor: ${totalSummary.NoTransactionCodeCount}\n`;
    text += `Total Outlet Yang No Sales: ${totalSummary.noSalesCount}\n`;
    text += `Total Omset: Rp. ${totalSummary.totalSales.toLocaleString()}\n`;
    text += `Rata-Rata Omset: Rp. ${
      totalSummary.uniqueCounterCount > 0
        ? (totalSummary.totalSales / totalSummary.uniqueCounterCount).toLocaleString()
        : "0"
    }\n`;

    transactions.forEach((txn) => {
      text += `\n*${txn._id}*\n`;
      text += `Total Quantity: ${txn.totalQuantity}\n`;
      text += `Total Outlet Yang Lapor: ${txn.uniqueCounterCount}\n`;
      text += `Total Outlet Yang Jual: ${txn.uniqueCounterCount - txn.noSalesCount}\n`;
      text += `Total Outlet Yang Tidak Lapor: ${txn.NoTransactionCodeCount}\n`;
      text += `Total Outlet Yang No Sales: ${txn.noSalesCount}\n`;
      text += `Total Omset: Rp. ${txn.totalSales.toLocaleString()}\n`;
      text += `Rata-Rata Omset: Rp. ${
        txn.uniqueCounterCount > 0 ? (txn.totalSales / txn.uniqueCounterCount).toLocaleString() : "0"
      }\n`;
      text += `Outlet Yang Tidak Lapor: ${(txn.NoTransactionCode?.length ? txn.NoTransactionCode.join(", ") : "–")}\n`;
      text += `Outlet Yang No Sales: ${(txn.noSalesCounters?.length ? txn.noSalesCounters.join(", ") : "–")}\n\n`;
    });

    return text;
  };

  const handleCopy = () => {
    const text = generateReportText();
    navigator.clipboard.writeText(text);
    message.success("Report copied to clipboard!");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 20 }}>
      <h2>Sales Report</h2>
      <div style={{ width: "80vw" }}>
        <Form form={form} layout="vertical">
          <Form.Item label="Date Range" required>
            {isMobile ? (
              <>
                <Form.Item
                  name="startDate"
                  rules={[{ required: true, message: "Please select a start date!" }]}
                  style={{ marginBottom: 8 }}
                >
                  <DatePicker style={{ width: "100%" }} placeholder="Start date" inputReadOnly onFocus={(e) => e.target.blur()} onClick={(e) => e.currentTarget.blur()} />
                </Form.Item>
                <Form.Item
                  name="endDate"
                  rules={[{ required: true, message: "Please select an end date!" }]}
                >
                  <DatePicker style={{ width: "100%" }} placeholder="End date" inputReadOnly onFocus={(e) => e.target.blur()} onClick={(e) => e.currentTarget.blur()} />
                </Form.Item>
              </>
            ) : (
              <Form.Item
                name="dateRange"
                rules={[{ required: true, message: "Please select a date range!" }]}
                style={{ marginBottom: 0 }}
              >
                <RangePicker style={{ width: "100%" }} inputReadOnly getPopupContainer={(trigger) => trigger.parentNode} />
              </Form.Item>
            )}
          </Form.Item>

          <Button type="primary" onClick={handleSubmit} loading={loading} style={{ marginBottom: 20 }}>
            Submit
          </Button>
        </Form>

        {transactions.length > 0 && (
          <Button
            type="default"
            icon={<CopyOutlined />}
            onClick={handleCopy}
            style={{ marginBottom: 20, backgroundColor: "#1890ff", color: "#fff" }}
          >
            Copy Report
          </Button>
        )}

        {transactions.length > 0 && (
          <>
            <div style={cardStyle}>
              <h2>Overall Summary</h2>
              <p><strong>Total Quantity:</strong> {totalSummary.totalQuantity}</p>
              <p><strong>Total Outlet Yang Lapor:</strong> {totalSummary.uniqueCounterCount}</p>
              <p><strong>Total Outlet Yang Jual:</strong> {totalSummary.uniqueCounterCount - totalSummary.noSalesCount}</p>
              <p><strong>Total Outlet Yang Tidak Lapor:</strong> {totalSummary.NoTransactionCodeCount}</p>
              <p><strong>Total Outlet Yang No Sales:</strong> {totalSummary.noSalesCount}</p>
              <p><strong>Total Omset:</strong> Rp. {totalSummary.totalSales.toLocaleString()}</p>
              <p><strong>Rata-Rata Omset:</strong> Rp. {(totalSummary.totalSales / Math.max(1, totalSummary.uniqueCounterCount)).toLocaleString()}</p>
            </div>

            {transactions.map((txn) => (
              <div key={txn._id} style={cardStyle}>
                <h3>{txn._id}</h3>
                <p><strong>Total Quantity:</strong> {txn.totalQuantity}</p>
                <p><strong>Total Outlet Yang Lapor:</strong> {txn.uniqueCounterCount}</p>
                <p><strong>Total Outlet Yang Jual:</strong> {txn.uniqueCounterCount - txn.noSalesCount}</p>
                <p><strong>Total Outlet Yang Tidak Lapor:</strong> {txn.NoTransactionCodeCount}</p>
                <p><strong>Total Outlet Yang No Sales:</strong> {txn.noSalesCount}</p>
                <p><strong>Total Omset:</strong> Rp. {txn.totalSales.toLocaleString()}</p>
                <p><strong>Rata-Rata Omset:</strong> Rp. {(txn.totalSales / Math.max(1, txn.uniqueCounterCount)).toLocaleString()}</p>
                <p><strong>Outlet Yang Tidak Lapor:</strong> {txn.NoTransactionCode?.length > 0 ? txn.NoTransactionCode.join(", ") : "–"}</p>
                <p><strong>Outlet Yang No Sales:</strong> {txn.noSalesCounters?.length > 0 ? txn.noSalesCounters.join(", ") : "–"}</p>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportPage;